import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '../ui/dialog';
import { Button } from '../ui/button';
import { 
  Gift, 
  Copy, 
  Check, 
  Users, 
  MessageSquare,
  Star,
  ChevronRight,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { creditsService } from '../../services/creditsService';
import FeedbackForm from './FeedbackForm';
import toast from 'react-hot-toast';

interface FreeCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FreeCreditsModal: React.FC<FreeCreditsModalProps> = ({ isOpen, onClose }) => {
  const { user, refreshUser } = useAuth();
  const [copied, setCopied] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [feedbackEligible, setFeedbackEligible] = useState(true);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  useEffect(() => {
    if (user && isOpen) {
      loadReferralLink();
      checkFeedbackEligibility();
    }
  }, [user, isOpen]);

  const loadReferralLink = async () => {
    try {
      const response = await creditsService.getReferralLink();
      setReferralLink(response.referral_link);
    } catch (error) {
      console.error('Failed to load referral link:', error);
      // Fallback to demo link
      const baseUrl = window.location.origin;
      setReferralLink(`${baseUrl}/register?ref=demo`);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const checkFeedbackEligibility = async () => {
    try {
      const response = await creditsService.checkFeedbackEligibility();
      setFeedbackEligible(response.eligible);
      setFeedbackMessage(response.message);
    } catch (error) {
      console.error('Failed to check feedback eligibility:', error);
      setFeedbackEligible(false);
      setFeedbackMessage('Unable to check feedback eligibility');
    }
  };

  const handleFeedbackSubmit = async () => {
    setShowFeedbackForm(false);
    
    // Show success message
    toast.success('🎉 Feedback submitted! 200 credits added to your account.');
    
    // Refresh user data to update credits
    try {
      await refreshUser();
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
    
    // Update feedback eligibility
    setFeedbackEligible(false);
    setFeedbackMessage('You have already submitted feedback this month');
  };

  if (showFeedbackForm) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              <span>Help Us Improve - Get 200 Credits!</span>
            </DialogTitle>
          </DialogHeader>
          <FeedbackForm 
            onSubmit={handleFeedbackSubmit}
            onCancel={() => setShowFeedbackForm(false)}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-center">
            <Gift className="w-6 h-6" style={{ color: '#30b3dd' }} />
            <span className="bg-clip-text text-transparent" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Get Free Credits!
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Referral Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-xl border border-blue-200/50 dark:border-blue-700/50"
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)', boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)' }}>
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Get 100 credits per invite
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Share your referral link with friends
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Your referral link:</p>
                <p className="text-sm font-mono break-all font-semibold text-black dark:text-white">
                  {referralLink || 'Loading...'}
                </p>
              </div>

              <Button 
                onClick={handleCopyLink}
                className="w-full text-white"
                style={{ 
                  background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                  backgroundSize: '200% 200%',
                  backgroundPosition: 'left center',
                  boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundPosition = 'right center';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(48, 179, 221, 0.4)';
                  e.currentTarget.style.transform = 'translateY(-1px) scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundPosition = 'left center';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(48, 179, 221, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                }}
                size="sm"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </>
                )}
              </Button>
            </div>
          </motion.div>

          {/* Feedback Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`p-4 rounded-xl border ${
              feedbackEligible 
                ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200/50 dark:border-green-700/50'
                : 'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/20 dark:to-gray-700/20 border-gray-200/50 dark:border-gray-600/50'
            }`}
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                feedbackEligible 
                  ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                  : 'bg-gradient-to-br from-gray-400 to-gray-500'
              }`}>
                {feedbackEligible ? (
                  <Star className="w-5 h-5 text-white" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <h3 className={`font-semibold ${
                  feedbackEligible 
                    ? 'text-gray-900 dark:text-white'
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  Get 200 credits by giving us feedback
                </h3>
                <p className={`text-sm ${
                  feedbackEligible 
                    ? 'text-gray-600 dark:text-gray-400'
                    : 'text-gray-500 dark:text-gray-500'
                }`}>
                  {feedbackEligible ? 'Help us improve our service' : feedbackMessage}
                </p>
              </div>
            </div>

            <Button 
              onClick={() => setShowFeedbackForm(true)}
              disabled={!feedbackEligible}
              className={`w-full ${
                feedbackEligible 
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
              size="sm"
            >
              {feedbackEligible ? (
                <>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Give Feedback
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Feedback Submitted This Month
                </>
              )}
            </Button>
          </motion.div>

          {/* Progress Indicator (Optional - for future use) */}
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Earn up to 1,000 free credits per month
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FreeCreditsModal;